import React, { FC, useMemo } from 'react';
import { WebsiteHeaderLayout_PhotoBackground, WebsitePhotoCropInfo } from '@apps/guest/websiteHeader';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useGetContainerDimensions } from '@apps/guest/packages/layout-engine/widgets/Custom/hooks/useGetContainerDimensions';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { LazyScreen } from '@apps/guest/packages/layout-engine/widgets/Custom/components/LazyScreen/LazyScreen';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { EventNameNavForStandaloneHeader, EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { BrannanNavigation, EventPageType } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteEventDesign } from '../../../../layout.types';
import { CroppedProgressiveImage, ProgressiveImage } from '../components/ProgressiveImage';

export interface PhotoBackgroundLayoutProps extends EventNameNavForStandaloneHeaderProps {
  websiteHeader: WebsiteHeaderLayout_PhotoBackground;
  showSkeleton?: boolean;
  showPlaceholder?: boolean;
  currentPageType: EventPageType | undefined;
  eventDesign: GuestSiteEventDesign;
  isInlineEditing?: boolean;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
  editableContentRef?: React.RefObject<HTMLDivElement>;
  forceRenderViewIn?: 'mobile' | 'desktop';
  scaleValue?: number;
}

type PhotoBackgroundRendererProps = {
  photo?: Photo;
  height: string;
  containerDimensions: {
    width: number;
    height: number;
  };
  isCompactView: boolean;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
};

type Photo = {
  id: string;
  url: string;
  cropInfo?: WebsitePhotoCropInfo;
};

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;

const PhotoBackgroundRenderer: FC<PhotoBackgroundRendererProps> = props => {
  const { photo, containerDimensions, height, isCompactView, renderedContainerHeight, renderedContainerWidth, children } = props;

  const containerWidth = renderedContainerWidth ?? containerDimensions.width;
  const containerHeight = renderedContainerHeight ?? containerDimensions.height;

  const derivedWidth = containerHeight * CONTAINER_ASPECT_RATIO;
  const derivedHeight = containerWidth / CONTAINER_ASPECT_RATIO;

  const { bestSuitableWidth, bestSuitableHeight } =
    derivedWidth < containerWidth
      ? { bestSuitableWidth: containerWidth, bestSuitableHeight: derivedHeight }
      : { bestSuitableWidth: derivedWidth, bestSuitableHeight: containerHeight };

  if (photo?.cropInfo) {
    return (
      <AnimatedImageBox
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems={isCompactView ? 'flex-start' : 'center'}
        backgroundColor="lightgray"
      >
        <CroppedProgressiveImage
          width={isCompactView ? containerWidth : bestSuitableWidth}
          height={isCompactView ? derivedHeight : bestSuitableHeight}
          photoUrl={photo.url}
          cropInfo={{ ...photo.cropInfo, unit: '%' }}
          containerStyles={{ position: 'absolute' }}
        />
        <Box
          width="100%"
          height="100%"
          position={isCompactView ? 'relative' : 'absolute'}
          zIndex={1}
        >
          {children}
        </Box>
      </AnimatedImageBox>
    );
  }

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
      overflow="hidden"
      backgroundColor="lightgray"
      minHeight={pxToRem(96)}
    >
      {photo?.url && (
        <ProgressiveImage
          photoUrl={photo.url}
          width="100%"
          height="100%"
          containerStyles={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      )}
      {children}
    </AnimatedImageBox>
  );
};

export const PhotoBackgroundLayout: FC<PhotoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];

  const { containerDimensions, containerRef } = useGetContainerDimensions();

  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && currentPageType !== EventPageType.welcome && pageIndex !== 0;
  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : { height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : '100vh', aspectRatio: `${CONTAINER_ASPECT_RATIO}` },
    [isCompactView, isInlineEditing, renderedContainerHeight],
  );

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  return (
    <Flex
      ref={containerRef}
      position="relative"
      zIndex={10}
      width="100%"
      aspectRatio={aspectRatio}
      height={height}
      //  minHeight ensures we will render header photo for non-welcome pages on mobile screen dimensions (EventMenu is positioned fixed so added a minHeight = 24px top padding + 48px menu height + 24px bottom padding)
      minHeight={pxToRem(96)}
      overflow="hidden"
    >
      <LazyScreen
        src={addRendition({ url: photo?.url, renditionSize: 'medium' })}
        width="100%"
        height={height}
        aspectRatio={aspectRatio}
        skeletonGroupOverrides={{
          Root: {
            props: {
              width: '100%',
              height: height,
              aspectRatio: aspectRatio,
            },
          },
          PlaceholderRoot: {
            props: {
              width: '100%',
              height: height,
              aspectRatio: aspectRatio,
              position: 'absolute',
            },
          },
        }}
        showSkeleton={showSkeleton}
      >
        {() => (
          <PhotoBackgroundRenderer
            photo={photo}
            containerDimensions={containerDimensions}
            height={height}
            isCompactView={isCompactView}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
          >
            <EventNameNavForStandaloneHeader
              eventDisplayName={websiteHeader.eventDisplayName}
              colors={websiteHeader.headerPhoto.colors}
              headerTextData={websiteHeader.headerPhoto.eventName}
              isCompactView={isCompactView}
              currentPageType={currentPageType}
              handleEditTextClick={handleEditTextClick}
              isContentEditable={isInlineEditing}
              editableContentRef={editableContentRef}
              forceRenderViewIn={forceRenderViewIn}
              scaleValue={scaleValue}
              pages={pages}
              pageSlug={pageSlug}
              {...restProps}
            />
          </PhotoBackgroundRenderer>
        )}
      </LazyScreen>
    </Flex>
  );
};

PhotoBackgroundLayout.displayName = 'PhotoBackgroundLayout';
